import React, { useEffect, useState } from 'react';
import { 
  Calendar, Search, Filter, Eye, Trash2, Loader2, 
  CheckCircle, XCircle, Clock, User, Plane, Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { api } from '../../utils/apiClient';
import { motion } from 'framer-motion';

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    today: 0,
    total_revenue: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadBookings();
    loadStats();
  }, []);

  useEffect(() => {
    filterAndSearchBookings();
  }, [filterStatus, searchTerm, bookings]);

  const loadBookings = async () => {
    try {
      const response = await api.get('/api/admin/bookings/all?limit=500');
      setBookings(response.bookings || []);
      setFilteredBookings(response.bookings || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/admin/bookings/stats/summary');
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filterAndSearchBookings = () => {
    let filtered = [...bookings];

    // Filtrar por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(b => b.status === filterStatus);
    }

    // Buscar por término
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        b.booking_id?.toLowerCase().includes(term) ||
        b.contact_email?.toLowerCase().includes(term) ||
        b.contact_name?.toLowerCase().includes(term) ||
        b.flight_number?.toLowerCase().includes(term)
      );
    }

    setFilteredBookings(filtered);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    if (!window.confirm(`¿Cambiar estado a "${newStatus}"?`)) return;

    try {
      await api.put(`/api/admin/bookings/${bookingId}/status`, { status: newStatus });
      loadBookings();
      loadStats();
      alert('Estado actualizado correctamente');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar el estado');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reserva? Esta acción no se puede deshacer.')) return;

    try {
      await api.delete(`/api/admin/bookings/${bookingId}`);
      loadBookings();
      loadStats();
      alert('Reserva eliminada correctamente');
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Error al eliminar la reserva');
    }
  };

  const viewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetails(true);
  };

  const exportToCSV = () => {
    const headers = ['ID Reserva', 'Fecha', 'Vuelo', 'Pasajeros', 'Contacto', 'Email', 'Teléfono', 'Total', 'Estado'];
    const rows = filteredBookings.map(b => [
      b.booking_id,
      new Date(b.created_at).toLocaleDateString(),
      b.flight_number,
      b.passengers?.length || 0,
      b.contact_name,
      b.contact_email,
      b.contact_phone,
      `$${b.total_price}`,
      b.status
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservas_aerotucan_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200'
    };

    const icons = {
      pending: Clock,
      confirmed: CheckCircle,
      cancelled: XCircle,
      completed: CheckCircle
    };

    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Completada'
    };

    const Icon = icons[status] || Clock;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3 mr-1" />
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Gestión de Reservas</h2>
        <Button
          onClick={exportToCSV}
          className="bg-green-600 hover:bg-green-700"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Reservas</p>
            <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Pendientes</p>
            <p className="text-3xl font-bold text-yellow-800">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Confirmadas</p>
            <p className="text-3xl font-bold text-green-800">{stats.confirmed}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Canceladas</p>
            <p className="text-3xl font-bold text-red-800">{stats.cancelled}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Ingresos</p>
            <p className="text-2xl font-bold text-purple-900">${stats.total_revenue.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Buscar por ID, nombre, email, vuelo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex space-x-2">
              {['all', 'pending', 'confirmed', 'cancelled'].map(status => (
                <Button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  className={filterStatus === status ? 'bg-blue-900' : ''}
                >
                  {status === 'all' ? 'Todas' : status === 'pending' ? 'Pendientes' : status === 'confirmed' ? 'Confirmadas' : 'Canceladas'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <p className="text-sm text-gray-600">
        Mostrando {filteredBookings.length} de {bookings.length} reservas
      </p>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">No se encontraron reservas</p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.booking_id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                    {/* Booking Info */}
                    <div>
                      <p className="text-sm text-gray-500">ID Reserva</p>
                      <p className="font-bold">{booking.booking_id}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(booking.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Flight Info */}
                    <div>
                      <p className="text-sm text-gray-500">Vuelo</p>
                      <p className="font-semibold flex items-center">
                        <Plane className="w-4 h-4 mr-1" />
                        {booking.flight_id?.split('-')[0] || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {booking.origin} → {booking.destination}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        📅 {booking.departure_date}
                      </p>
                    </div>

                    {/* Passengers */}
                    <div>
                      <p className="text-sm text-gray-500">Pasajeros</p>
                      <p className="font-semibold flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {booking.passengers?.length || 0}
                      </p>
                      <p className="text-sm text-gray-600">
                        {booking.contact_person?.first_name} {booking.contact_person?.last_name}
                      </p>
                    </div>

                    {/* Contact */}
                    <div>
                      <p className="text-sm text-gray-500">Contacto</p>
                      <p className="text-sm">{booking.contact_person?.email || 'N/A'}</p>
                      <p className="text-sm text-gray-600">{booking.contact_person?.phone || 'N/A'}</p>
                    </div>

                    {/* Price & Status */}
                    <div>
                      <p className="text-sm text-gray-500">Total</p>
                      <p className="text-xl font-bold text-green-600">
                        ${booking.total_price?.toLocaleString()} MXN
                      </p>
                      <div className="mt-2">
                        {getStatusBadge(booking.status)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button
                      size="sm"
                      onClick={() => viewDetails(booking)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    {booking.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(booking.booking_id, 'confirmed')}
                        className="bg-green-600 hover:bg-green-700"
                        title="Confirmar"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {booking.status !== 'cancelled' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(booking.booking_id, 'cancelled')}
                        className="bg-amber-600 hover:bg-amber-700"
                        title="Cancelar"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      onClick={() => handleDelete(booking.booking_id)}
                      className="bg-red-600 hover:bg-red-700"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold">Detalles de Reserva</h3>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="outline"
                  size="sm"
                >
                  Cerrar
                </Button>
              </div>

              <div className="space-y-6">
                {/* Booking Info */}
                <div>
                  <h4 className="font-bold mb-2">Información de Reserva</h4>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">ID:</p>
                      <p className="font-semibold">{selectedBooking.booking_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Estado:</p>
                      {getStatusBadge(selectedBooking.status)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Fecha de Creación:</p>
                      <p>{new Date(selectedBooking.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total:</p>
                      <p className="text-xl font-bold text-green-600">
                        ${selectedBooking.total_price?.toLocaleString()} MXN
                      </p>
                    </div>
                  </div>
                </div>

                {/* Flight Info */}
                <div>
                  <h4 className="font-bold mb-2">Información de Vuelo</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p><strong>ID Vuelo:</strong> {selectedBooking.flight_id || 'N/A'}</p>
                    <p><strong>Ruta:</strong> {selectedBooking.origin} → {selectedBooking.destination}</p>
                    <p><strong>Fecha de salida:</strong> {selectedBooking.departure_date || 'N/A'}</p>
                    <p><strong>Hora de salida:</strong> {selectedBooking.departure_time || 'N/A'}</p>
                    <p><strong>Tipo de viaje:</strong> {selectedBooking.trip_type === 'one-way' ? 'Solo ida' : 'Ida y vuelta'}</p>
                    {selectedBooking.return_flight_id && (
                      <>
                        <p><strong>Vuelo de regreso:</strong> {selectedBooking.return_flight_id}</p>
                        <p><strong>Fecha de regreso:</strong> {selectedBooking.return_date}</p>
                        <p><strong>Hora de regreso:</strong> {selectedBooking.return_time}</p>
                      </>
                    )}
                    <p><strong>Precio por persona:</strong> ${selectedBooking.price_per_person?.toLocaleString()} {selectedBooking.currency}</p>
                    <p><strong>Número de pasajeros:</strong> {selectedBooking.num_passengers}</p>
                    <p><strong>Total:</strong> <span className="text-xl font-bold text-green-600">${selectedBooking.total_price?.toLocaleString()} {selectedBooking.currency}</span></p>
                  </div>
                </div>

                {/* Passengers */}
                <div>
                  <h4 className="font-bold mb-2">Pasajeros ({selectedBooking.passengers?.length || 0})</h4>
                  <div className="space-y-2">
                    {selectedBooking.passengers?.map((passenger, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <p><strong>Nombre:</strong> {passenger.first_name} {passenger.last_name}</p>
                        <p><strong>Email:</strong> {passenger.email || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {passenger.phone || 'N/A'}</p>
                        <p><strong>Tipo:</strong> {passenger.passenger_type === 'adult' ? 'Adulto' : passenger.passenger_type === 'child' ? 'Niño' : 'Bebé'}</p>
                        <p><strong>Documento:</strong> {passenger.id_type} - {passenger.id_number}</p>
                        <p><strong>Nacionalidad:</strong> {passenger.nationality}</p>
                        {passenger.date_of_birth && <p><strong>Fecha de nacimiento:</strong> {passenger.date_of_birth}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <h4 className="font-bold mb-2">Información de Contacto</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Nombre:</strong> {selectedBooking.contact_person?.first_name} {selectedBooking.contact_person?.last_name}</p>
                    <p><strong>Email:</strong> {selectedBooking.contact_person?.email}</p>
                    <p><strong>Teléfono:</strong> {selectedBooking.contact_person?.phone}</p>
                    <p><strong>Nacionalidad:</strong> {selectedBooking.contact_person?.nationality}</p>
                    <p><strong>Tipo de ID:</strong> {selectedBooking.contact_person?.id_type}</p>
                    <p><strong>Número de ID:</strong> {selectedBooking.contact_person?.id_number}</p>
                  </div>
                </div>

                {/* Payment & Status */}
                <div>
                  <h4 className="font-bold mb-2">Estado de Pago</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Estado de reserva:</strong> {getStatusBadge(selectedBooking.status)}</p>
                    <p><strong>Estado de pago:</strong> <span className={`font-semibold ${selectedBooking.payment_status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>{selectedBooking.payment_status === 'completed' ? 'Completado' : selectedBooking.payment_status === 'pending' ? 'Pendiente' : 'Sin pago'}</span></p>
                    <p><strong>Método de pago:</strong> {selectedBooking.payment_method || 'No especificado'}</p>
                  </div>
                </div>

                {/* Special Requests */}
                {selectedBooking.special_requests && (
                  <div>
                    <h4 className="font-bold mb-2">Solicitudes Especiales</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>{selectedBooking.special_requests}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div>
                  <h4 className="font-bold mb-2">Información de Sistema</h4>
                  <div className="bg-gray-50 p-4 rounded-lg text-sm">
                    <p><strong>Fecha de creación:</strong> {new Date(selectedBooking.created_at).toLocaleString('es-MX')}</p>
                    <p><strong>Última actualización:</strong> {new Date(selectedBooking.updated_at).toLocaleString('es-MX')}</p>
                    <p><strong>ID único:</strong> {selectedBooking.booking_id}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BookingsManagement;
